import React, { useEffect, useRef } from 'react';
import { X, Info, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const TYPE_CONFIG = {
  info: { icon: Info, border: 'border-blue-300 dark:border-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', iconColor: 'text-blue-500' },
  warning: { icon: AlertTriangle, border: 'border-orange-300 dark:border-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', iconColor: 'text-orange-500' },
  success: { icon: CheckCircle, border: 'border-green-300 dark:border-green-600', bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', iconColor: 'text-green-500' },
  error: { icon: AlertCircle, border: 'border-red-300 dark:border-red-600', bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', iconColor: 'text-red-500' }
};

const AnnouncementDrawer = ({ isOpen, onClose, announcements, onDismiss, onDismissAll }) => {
  const panelRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Delay so the opening click doesn't immediately close it
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClick), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="fixed top-16 right-4 z-50 w-[90vw] sm:w-96 max-h-[70vh] flex flex-col rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <span className="text-sm font-semibold font-rasa text-gray-900 dark:text-white">
          Announcements
          {announcements.length > 0 && (
            <span className="ml-2 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
              {announcements.length}
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          {announcements.some(a => a.dismissible) && (
            <button
              onClick={onDismissAll}
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-rasa"
            >
              Dismiss all
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={16} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {announcements.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <p className="text-sm font-rasa">No announcements</p>
          </div>
        ) : (
          announcements.map(a => {
            const config = TYPE_CONFIG[a.announcement_type] || TYPE_CONFIG.info;
            const Icon = config.icon;

            return (
              <div
                key={a.id}
                className={`${config.bg} border ${config.border} rounded-lg shadow-sm p-3 transition-all hover:shadow-md`}
              >
                <div className="flex items-start gap-2.5">
                  <Icon size={18} className={`${config.iconColor} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-bold font-rasa ${config.text}`}>{a.title}</h3>
                    {a.body && (
                      <div className={`text-sm font-rasa mt-1 ${config.text} opacity-80 prose prose-sm dark:prose-invert max-w-none`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body}</ReactMarkdown>
                      </div>
                    )}
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 font-rasa">
                      {new Date(a.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {a.dismissible && (
                    <button
                      onClick={() => onDismiss(a.id)}
                      className="text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 flex-shrink-0"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AnnouncementDrawer;
